import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { differenceInYears } from 'date-fns';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { extractWallClockTimeStrict } from '@bodyanalytics/data-models-ui';

export function minimumAgeValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateOfBirth = control.value;
    if (!dateOfBirth) {
      return null; // return null if no date is provided
    }

    const age = differenceInYears(new Date(), new Date(dateOfBirth));
    return age >= minAge ? null : { minimumAge: { requiredAge: minAge, actualAge: age } };
  };
}
export function atLeastOneCheckboxValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const isRemindByEmail = formGroup.get('isRemindByEmail')?.value;
    const isRemindBySms = formGroup.get('isRemindBySms')?.value;

    if (isRemindByEmail || isRemindBySms) {
      return null; // valid if at least one is true
    } else {
      return { atLeastOneRequired: true }; // invalid if both are false
    }
  };
}
export function validYearValidator(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // required validator will handle empty case
    }

    const date = new Date(value);
    // If the date is invalid, return an error
    if (isNaN(date.getTime())) {
      return { invalidYear: true };
    }

    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();

    // Adjust the conditions based on your valid year constraints.
    if (year < 1900 || year > currentYear) {
      return { invalidYear: true };
    }

    return null;
  };
}

/**
 * Validates a US phone number using the same `libphonenumber-js` rules the
 * backend's `@IsPhoneNumber('US')` DTO decorator enforces (#1929 — see
 * `manage-booking-api/src/lib/dto/process-booking-flow.dto.ts`).
 *
 * The dashed shape check (`Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)`) only
 * confirms the input LOOKS like a phone number; it happily accepts fictional
 * numbers like `555-123-4567` (a 555 area code is not assigned in the NANP).
 * That mismatch let a customer pick a slot and enter payment details before
 * `process-booking` 400'd on the same number the frontend had just accepted.
 * This validator agrees with the backend up front, at the phone field.
 */
export function usPhoneNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null; // required validator handles the empty case
  }

  return isValidPhoneNumber(value, 'US') ? null : { invalidPhoneNumber: true };
}

/**
 * #2120 — cross-field FormGroup validator: the event's end time must be
 * strictly after its start time. Neither the operator create-event form nor
 * the admin create/edit event forms had ANY ordering check, so
 * `startTime=14:00, endTime=09:00` sailed through to the API (which, pre
 * #2120, answered 201 and persisted a silently backwards event).
 *
 * Parsing delegates to the ONE shared strict wall-clock parser
 * (`extractWallClockTimeStrict`, ct-time.util.ts / ADR-0018 addendum), which
 * covers every shape our event forms actually hold: zero-padded 24-hour
 * "HH:mm" (`<input type="time">` — operator create-event, admin
 * event-create), non-padded "H:mm" (the admin edit form's `eventTimes`
 * dropdown values, e.g. "8:00" / "13:30"), 12-hour "h:mm AM/PM", and — via
 * `allowCtIso` — a stored CT-ISO datetime that reaches a control unconverted.
 *
 * Missing/unparseable values return null — `Validators.required` and the
 * field-level format validation own those failure modes; this validator only
 * answers the ordering question. Mirrors the backend's
 * `IsEndTimeAfterStartTime` (common-api) so both layers agree.
 */
export function eventTimeOrderValidator(startControlName = 'startTime', endControlName = 'endTime'): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const start = extractWallClockTimeStrict(group.get(startControlName)?.value, { allowCtIso: true });
    const end = extractWallClockTimeStrict(group.get(endControlName)?.value, { allowCtIso: true });
    if (start === null || end === null) {
      return null;
    }
    // Zero-padded "HH:mm" strings compare lexicographically = chronologically.
    return end > start ? null : { endTimeNotAfterStart: true };
  };
}

export function noScriptTagValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null; // If empty, let the 'required' validator handle it
  }

  // Check if the value contains a <script> tag.
  // This regex is a simple check. Adjust as needed.
  const scriptTagPattern = /<script\b[^<]*>(.*?)<\/script>/i;

  if (scriptTagPattern.test(value)) {
    return { disallowedContent: true };
  }
  return null;
}

/**
 * #1931: `noScriptTagValidator` only blocks a literal `<script>...</script>`
 * string — payloads like `<img onerror=alert(1)>`, bare angle brackets, or
 * other HTML-special characters sailed through address/city fields
 * untouched. This validator instead allowlists the character set that is
 * actually valid for a free-text postal address/city field: letters
 * (Unicode-aware, so accented/diacritic letters in legitimate USPS-valid
 * city/street names like `Bayamón`, `Mayagüez`, or `Provo Cañon Rd` are not
 * rejected), numbers, whitespace, and common address punctuation
 * (`# - . , ' / '` — including the curly apostrophe `’` that iOS/mobile
 * smart-punctuation substitutes for a typed straight apostrophe, e.g.
 * `O'Brien Ln`). Anything outside that set (angle brackets, ampersands,
 * semicolons, backticks, double quotes, etc.) is rejected.
 *
 * This is client-side, defense-in-depth UX hardening only, matching the
 * character-class approach already used for firstName/lastName — the
 * server remains the source of truth for sanitization/validation.
 */
const SAFE_ADDRESS_CHARACTERS_PATTERN = /^[\p{L}\p{N}\s#.,'’/-]*$/u;

export function safeAddressCharactersValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null; // If empty, let the 'required' validator handle it
  }

  return SAFE_ADDRESS_CHARACTERS_PATTERN.test(value) ? null : { disallowedContent: true };
}
